import React, { useState } from 'react';
import { VatSettings } from '../../../types';
import VatSettingsForm from './VatSettingsForm';
import './VatManagement.css';

interface VatManagementProps {
  settings: VatSettings | null;
  loading?: boolean;
  onUpdateSettings: (settings: Partial<VatSettings>) => void;
}

const VatManagement: React.FC<VatManagementProps> = ({
  settings,
  loading = false,
  onUpdateSettings
}) => {
  const [isEditing, setIsEditing] = useState(false);

  const handleSave = (updatedSettings: Partial<VatSettings>) => {
    onUpdateSettings(updatedSettings);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setIsEditing(false);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  if (loading) {
    return (
      <div className="vat-management">
        <div className="vat-management__loading">
          <div className="loading-spinner"></div>
          <p>Loading VAT settings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="vat-management">
      <div className="vat-management__header">
        <h3>VAT Management</h3>
        <p className="vat-management__description">
          Configure VAT settings for your business. VAT will be calculated and displayed to customers based on these settings.
        </p>
      </div>

      {isEditing ? (
        <VatSettingsForm
          settings={settings}
          onSubmit={handleSave}
          onCancel={handleCancel}
          loading={loading}
        />
      ) : (
        <div className="vat-management__display">
          <div className="vat-settings-card">
            <div className="vat-settings-card__header">
              <h4>Current VAT Settings</h4>
              <button
                className="btn btn--secondary btn--small"
                onClick={handleEdit}
                disabled={loading}
              >
                Edit Settings
              </button>
            </div>

            <div className="vat-settings-card__content">
              <div className="vat-settings-card__row">
                <div className="vat-settings-card__field">
                  <label>VAT Status</label>
                  <div className={`vat-status vat-status--${settings?.isEnabled ? 'enabled' : 'disabled'}`}>
                    {settings?.isEnabled ? 'Enabled' : 'Disabled'}
                  </div>
                </div>

                <div className="vat-settings-card__field">
                  <label>VAT Percentage</label>
                  <div className="vat-percentage">
                    {settings?.percentage || 0}%
                  </div>
                </div>
              </div>

              <div className="vat-settings-card__row">
                <div className="vat-settings-card__field">
                  <label>Pricing Mode</label>
                  <div className="pricing-mode">
                    {settings?.isInclusive ? 'Inclusive (VAT included in prices)' : 'Exclusive (VAT added on top)'}
                  </div>
                </div>

                <div className="vat-settings-card__field">
                  <label>System Status</label>
                  <div className={`system-status system-status--${settings?.isActive ? 'active' : 'inactive'}`}>
                    {settings?.isActive ? 'Active' : 'Inactive'}
                  </div>
                </div>
              </div>

              {settings?.isEnabled && (
                <div className="vat-settings-card__preview">
                  <h5>VAT Calculation Preview</h5>
                  <div className="vat-preview">
                    <div className="vat-preview__example">
                      <span>Subtotal: €100.00</span>
                      <span>VAT ({settings.percentage}%): €{(100 * settings.percentage / 100).toFixed(2)}</span>
                      <span className="vat-preview__total">
                        Total: €{(100 + (100 * settings.percentage / 100)).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VatManagement;
